import type { ColorScheme, ThemeMode } from "./types";

/**
 * The founder-approved 5-scheme set (D-08): Trust (default) + Atlas + Slate + Teal +
 * Violet. Every scheme is full-token (all ColorSchemeTokens keys, light + dark) and the
 * VALUES deep-equal design/claude-design/tokens.approved.json — the single source the
 * token-contract + theme-config tests parse. Trust is DEFAULT_COLOR_SCHEME_ID; its token
 * maps live in the JSON `default` block, and the static tokens.css :root/.dark carries
 * the SAME Trust default values so the applied default and the static CSS agree (site
 * SSR first paint). The Atlas identity (09.3) is a selectable scheme at `themes.atlas`.
 * Stored "default" / unknown scheme ids resolve to Trust via the ThemeProvider unknown-id
 * fallback. The retired demo schemes (ocean/forest/sunset/monochrome) were removed in
 * 08.9-03.
 */
export const colorSchemes: ColorScheme[] = [
  {
    id: "atlas",
    name: "Atlas",
    light: {
      background: "oklch(0.972 0.002 260)",
      foreground: "oklch(0.3 0.03 230)",
      card: "oklch(0.995 0.001 260)",
      cardForeground: "oklch(0.3 0.03 230)",
      popover: "oklch(0.995 0.001 260)",
      popoverForeground: "oklch(0.3 0.03 230)",
      primary: "oklch(0.555 0.14 40)",
      primaryForeground: "oklch(0.99 0.001 260)",
      secondary: "oklch(0.95 0.006 260)",
      secondaryForeground: "oklch(0.3 0.03 230)",
      muted: "oklch(0.96 0.004 260)",
      mutedForeground: "oklch(0.48 0.02 260)",
      accent: "oklch(0.95 0.006 260)",
      accentForeground: "oklch(0.3 0.03 230)",
      destructive: "oklch(0.577 0.245 27.3)",
      destructiveForeground: "oklch(0.99 0.001 260)",
      border: "oklch(0.905 0.003 260)",
      input: "oklch(0.905 0.003 260)",
      ring: "oklch(0.555 0.14 40)",
      sidebar: "oklch(0.995 0.001 260)",
      sidebarForeground: "oklch(0.3 0.03 230)",
      sidebarPrimary: "oklch(0.45 0.07 200)",
      sidebarPrimaryForeground: "oklch(0.99 0.001 260)",
      sidebarAccent: "oklch(0.95 0.006 260)",
      sidebarAccentForeground: "oklch(0.3 0.03 230)",
      sidebarBorder: "oklch(0.905 0.003 260)",
      sidebarRing: "oklch(0.57 0.14 40)",
      chart1: "oklch(0.57 0.14 40)",
      chart2: "oklch(0.57 0.14 40 / 0.55)",
      chart3: "oklch(0.57 0.14 40 / 0.3)",
      chart4: "oklch(0.45 0.07 200)",
      chart5: "oklch(0.48 0.02 260)",
    },
    dark: {
      background: "oklch(0.2 0.025 230)",
      foreground: "oklch(0.93 0.012 80)",
      card: "oklch(0.24 0.025 230)",
      cardForeground: "oklch(0.93 0.012 80)",
      popover: "oklch(0.24 0.025 230)",
      popoverForeground: "oklch(0.93 0.012 80)",
      primary: "oklch(0.68 0.13 40)",
      primaryForeground: "oklch(0.18 0.02 230)",
      secondary: "oklch(0.28 0.025 230)",
      secondaryForeground: "oklch(0.93 0.012 80)",
      muted: "oklch(0.27 0.025 230)",
      mutedForeground: "oklch(0.65 0.02 230)",
      accent: "oklch(0.28 0.025 230)",
      accentForeground: "oklch(0.93 0.012 80)",
      destructive: "oklch(0.52 0.2 27.3)",
      destructiveForeground: "oklch(0.93 0.012 80)",
      border: "oklch(0.32 0.025 230)",
      input: "oklch(0.17 0.02 230)",
      ring: "oklch(0.68 0.13 40)",
      sidebar: "oklch(0.24 0.025 230)",
      sidebarForeground: "oklch(0.93 0.012 80)",
      sidebarPrimary: "oklch(0.6 0.07 200)",
      sidebarPrimaryForeground: "oklch(0.18 0.02 230)",
      sidebarAccent: "oklch(0.28 0.025 230)",
      sidebarAccentForeground: "oklch(0.93 0.012 80)",
      sidebarBorder: "oklch(0.32 0.025 230)",
      sidebarRing: "oklch(0.68 0.13 40)",
      chart1: "oklch(0.68 0.13 40)",
      chart2: "oklch(0.68 0.13 40 / 0.55)",
      chart3: "oklch(0.68 0.13 40 / 0.3)",
      chart4: "oklch(0.6 0.07 200)",
      chart5: "oklch(0.65 0.02 230)",
    },
  },
  {
    id: "trust",
    name: "Trust",
    light: {
      background: "oklch(0.99 0.004 255)",
      foreground: "oklch(0.21 0.02 260)",
      card: "oklch(1 0.002 255)",
      cardForeground: "oklch(0.21 0.02 260)",
      popover: "oklch(1 0.002 255)",
      popoverForeground: "oklch(0.21 0.02 260)",
      primary: "oklch(0.55 0.16 255)",
      primaryForeground: "oklch(0.99 0.005 255)",
      secondary: "oklch(0.95 0.01 255)",
      secondaryForeground: "oklch(0.27 0.03 260)",
      muted: "oklch(0.96 0.008 255)",
      mutedForeground: "oklch(0.5 0.02 260)",
      accent: "oklch(0.95 0.01 255)",
      accentForeground: "oklch(0.27 0.03 260)",
      destructive: "oklch(0.577 0.245 27.3)",
      destructiveForeground: "oklch(0.99 0.005 255)",
      border: "oklch(0.91 0.01 255)",
      input: "oklch(0.91 0.01 255)",
      ring: "oklch(0.55 0.16 255)",
      sidebar: "oklch(0.97 0.006 255)",
      sidebarForeground: "oklch(0.21 0.02 260)",
      sidebarPrimary: "oklch(0.55 0.16 255)",
      sidebarPrimaryForeground: "oklch(0.99 0.005 255)",
      sidebarAccent: "oklch(0.95 0.01 255)",
      sidebarAccentForeground: "oklch(0.27 0.03 260)",
      sidebarBorder: "oklch(0.91 0.01 255)",
      sidebarRing: "oklch(0.55 0.16 255)",
      chart1: "oklch(0.55 0.16 255)",
      chart2: "oklch(0.6 0.13 220)",
      chart3: "oklch(0.5 0.12 280)",
      chart4: "oklch(0.68 0.14 200)",
      chart5: "oklch(0.62 0.15 300)",
    },
    dark: {
      background: "oklch(0.16 0.02 260)",
      foreground: "oklch(0.95 0.01 255)",
      card: "oklch(0.2 0.02 260)",
      cardForeground: "oklch(0.95 0.01 255)",
      popover: "oklch(0.2 0.02 260)",
      popoverForeground: "oklch(0.95 0.01 255)",
      primary: "oklch(0.66 0.16 250)",
      primaryForeground: "oklch(0.16 0.02 260)",
      secondary: "oklch(0.28 0.03 260)",
      secondaryForeground: "oklch(0.93 0.01 255)",
      muted: "oklch(0.27 0.02 260)",
      mutedForeground: "oklch(0.7 0.02 255)",
      accent: "oklch(0.28 0.03 260)",
      accentForeground: "oklch(0.93 0.01 255)",
      destructive: "oklch(0.52 0.2 27.3)",
      destructiveForeground: "oklch(0.95 0.01 255)",
      border: "oklch(0.3 0.02 260)",
      input: "oklch(0.3 0.02 260)",
      ring: "oklch(0.66 0.16 250)",
      sidebar: "oklch(0.2 0.02 260)",
      sidebarForeground: "oklch(0.95 0.01 255)",
      sidebarPrimary: "oklch(0.66 0.16 250)",
      sidebarPrimaryForeground: "oklch(0.16 0.02 260)",
      sidebarAccent: "oklch(0.28 0.03 260)",
      sidebarAccentForeground: "oklch(0.93 0.01 255)",
      sidebarBorder: "oklch(0.3 0.02 260)",
      sidebarRing: "oklch(0.66 0.16 250)",
      chart1: "oklch(0.66 0.16 250)",
      chart2: "oklch(0.7 0.13 220)",
      chart3: "oklch(0.6 0.12 280)",
      chart4: "oklch(0.75 0.14 200)",
      chart5: "oklch(0.68 0.15 300)",
    },
  },
  {
    id: "slate",
    name: "Slate",
    light: {
      background: "oklch(0.99 0.003 250)",
      foreground: "oklch(0.22 0.015 255)",
      card: "oklch(1 0.002 250)",
      cardForeground: "oklch(0.22 0.015 255)",
      popover: "oklch(1 0.002 250)",
      popoverForeground: "oklch(0.22 0.015 255)",
      primary: "oklch(0.45 0.07 255)",
      primaryForeground: "oklch(0.99 0.004 250)",
      secondary: "oklch(0.95 0.008 250)",
      secondaryForeground: "oklch(0.28 0.02 255)",
      muted: "oklch(0.96 0.006 250)",
      mutedForeground: "oklch(0.5 0.015 255)",
      accent: "oklch(0.95 0.008 250)",
      accentForeground: "oklch(0.28 0.02 255)",
      destructive: "oklch(0.577 0.245 27.3)",
      destructiveForeground: "oklch(0.99 0.004 250)",
      border: "oklch(0.91 0.008 250)",
      input: "oklch(0.91 0.008 250)",
      ring: "oklch(0.45 0.07 255)",
      sidebar: "oklch(0.97 0.005 250)",
      sidebarForeground: "oklch(0.22 0.015 255)",
      sidebarPrimary: "oklch(0.45 0.07 255)",
      sidebarPrimaryForeground: "oklch(0.99 0.004 250)",
      sidebarAccent: "oklch(0.95 0.008 250)",
      sidebarAccentForeground: "oklch(0.28 0.02 255)",
      sidebarBorder: "oklch(0.91 0.008 250)",
      sidebarRing: "oklch(0.45 0.07 255)",
      chart1: "oklch(0.45 0.07 255)",
      chart2: "oklch(0.55 0.06 240)",
      chart3: "oklch(0.5 0.05 270)",
      chart4: "oklch(0.62 0.06 230)",
      chart5: "oklch(0.58 0.07 290)",
    },
    dark: {
      background: "oklch(0.17 0.015 255)",
      foreground: "oklch(0.94 0.008 250)",
      card: "oklch(0.21 0.015 255)",
      cardForeground: "oklch(0.94 0.008 250)",
      popover: "oklch(0.21 0.015 255)",
      popoverForeground: "oklch(0.94 0.008 250)",
      primary: "oklch(0.62 0.09 255)",
      primaryForeground: "oklch(0.16 0.015 255)",
      secondary: "oklch(0.28 0.02 255)",
      secondaryForeground: "oklch(0.92 0.008 250)",
      muted: "oklch(0.27 0.015 255)",
      mutedForeground: "oklch(0.7 0.015 250)",
      accent: "oklch(0.28 0.02 255)",
      accentForeground: "oklch(0.92 0.008 250)",
      destructive: "oklch(0.52 0.2 27.3)",
      destructiveForeground: "oklch(0.94 0.008 250)",
      border: "oklch(0.3 0.015 255)",
      input: "oklch(0.3 0.015 255)",
      ring: "oklch(0.62 0.09 255)",
      sidebar: "oklch(0.21 0.015 255)",
      sidebarForeground: "oklch(0.94 0.008 250)",
      sidebarPrimary: "oklch(0.62 0.09 255)",
      sidebarPrimaryForeground: "oklch(0.16 0.015 255)",
      sidebarAccent: "oklch(0.28 0.02 255)",
      sidebarAccentForeground: "oklch(0.92 0.008 250)",
      sidebarBorder: "oklch(0.3 0.015 255)",
      sidebarRing: "oklch(0.62 0.09 255)",
      chart1: "oklch(0.62 0.09 255)",
      chart2: "oklch(0.68 0.07 240)",
      chart3: "oklch(0.6 0.06 270)",
      chart4: "oklch(0.72 0.07 230)",
      chart5: "oklch(0.66 0.08 290)",
    },
  },
  {
    id: "teal",
    name: "Teal",
    light: {
      background: "oklch(0.99 0.004 200)",
      foreground: "oklch(0.21 0.02 215)",
      card: "oklch(1 0.002 200)",
      cardForeground: "oklch(0.21 0.02 215)",
      popover: "oklch(1 0.002 200)",
      popoverForeground: "oklch(0.21 0.02 215)",
      primary: "oklch(0.52 0.11 195)",
      primaryForeground: "oklch(0.99 0.005 200)",
      secondary: "oklch(0.94 0.012 200)",
      secondaryForeground: "oklch(0.27 0.03 215)",
      muted: "oklch(0.96 0.008 200)",
      mutedForeground: "oklch(0.49 0.02 210)",
      accent: "oklch(0.94 0.012 200)",
      accentForeground: "oklch(0.27 0.03 215)",
      destructive: "oklch(0.577 0.245 27.3)",
      destructiveForeground: "oklch(0.99 0.005 200)",
      border: "oklch(0.9 0.012 200)",
      input: "oklch(0.9 0.012 200)",
      ring: "oklch(0.52 0.11 195)",
      sidebar: "oklch(0.97 0.006 200)",
      sidebarForeground: "oklch(0.21 0.02 215)",
      sidebarPrimary: "oklch(0.52 0.11 195)",
      sidebarPrimaryForeground: "oklch(0.99 0.005 200)",
      sidebarAccent: "oklch(0.94 0.012 200)",
      sidebarAccentForeground: "oklch(0.27 0.03 215)",
      sidebarBorder: "oklch(0.9 0.012 200)",
      sidebarRing: "oklch(0.52 0.11 195)",
      chart1: "oklch(0.52 0.11 195)",
      chart2: "oklch(0.6 0.1 175)",
      chart3: "oklch(0.55 0.09 215)",
      chart4: "oklch(0.68 0.1 160)",
      chart5: "oklch(0.62 0.11 230)",
    },
    dark: {
      background: "oklch(0.16 0.02 215)",
      foreground: "oklch(0.94 0.01 200)",
      card: "oklch(0.2 0.02 215)",
      cardForeground: "oklch(0.94 0.01 200)",
      popover: "oklch(0.2 0.02 215)",
      popoverForeground: "oklch(0.94 0.01 200)",
      primary: "oklch(0.68 0.12 190)",
      primaryForeground: "oklch(0.15 0.02 215)",
      secondary: "oklch(0.28 0.03 215)",
      secondaryForeground: "oklch(0.92 0.01 200)",
      muted: "oklch(0.27 0.02 215)",
      mutedForeground: "oklch(0.7 0.02 200)",
      accent: "oklch(0.28 0.03 215)",
      accentForeground: "oklch(0.92 0.01 200)",
      destructive: "oklch(0.52 0.2 27.3)",
      destructiveForeground: "oklch(0.94 0.01 200)",
      border: "oklch(0.3 0.02 215)",
      input: "oklch(0.3 0.02 215)",
      ring: "oklch(0.68 0.12 190)",
      sidebar: "oklch(0.2 0.02 215)",
      sidebarForeground: "oklch(0.94 0.01 200)",
      sidebarPrimary: "oklch(0.68 0.12 190)",
      sidebarPrimaryForeground: "oklch(0.15 0.02 215)",
      sidebarAccent: "oklch(0.28 0.03 215)",
      sidebarAccentForeground: "oklch(0.92 0.01 200)",
      sidebarBorder: "oklch(0.3 0.02 215)",
      sidebarRing: "oklch(0.68 0.12 190)",
      chart1: "oklch(0.68 0.12 190)",
      chart2: "oklch(0.72 0.1 175)",
      chart3: "oklch(0.64 0.09 215)",
      chart4: "oklch(0.76 0.1 160)",
      chart5: "oklch(0.7 0.11 230)",
    },
  },
  {
    id: "violet",
    name: "Violet",
    light: {
      background: "oklch(0.99 0.004 300)",
      foreground: "oklch(0.22 0.02 300)",
      card: "oklch(1 0.002 300)",
      cardForeground: "oklch(0.22 0.02 300)",
      popover: "oklch(1 0.002 300)",
      popoverForeground: "oklch(0.22 0.02 300)",
      primary: "oklch(0.53 0.18 295)",
      primaryForeground: "oklch(0.99 0.005 300)",
      secondary: "oklch(0.95 0.012 300)",
      secondaryForeground: "oklch(0.28 0.03 300)",
      muted: "oklch(0.96 0.008 300)",
      mutedForeground: "oklch(0.5 0.02 300)",
      accent: "oklch(0.95 0.012 300)",
      accentForeground: "oklch(0.28 0.03 300)",
      destructive: "oklch(0.577 0.245 27.3)",
      destructiveForeground: "oklch(0.99 0.005 300)",
      border: "oklch(0.91 0.012 300)",
      input: "oklch(0.91 0.012 300)",
      ring: "oklch(0.53 0.18 295)",
      sidebar: "oklch(0.97 0.006 300)",
      sidebarForeground: "oklch(0.22 0.02 300)",
      sidebarPrimary: "oklch(0.53 0.18 295)",
      sidebarPrimaryForeground: "oklch(0.99 0.005 300)",
      sidebarAccent: "oklch(0.95 0.012 300)",
      sidebarAccentForeground: "oklch(0.28 0.03 300)",
      sidebarBorder: "oklch(0.91 0.012 300)",
      sidebarRing: "oklch(0.53 0.18 295)",
      chart1: "oklch(0.53 0.18 295)",
      chart2: "oklch(0.6 0.15 320)",
      chart3: "oklch(0.55 0.14 270)",
      chart4: "oklch(0.66 0.16 310)",
      chart5: "oklch(0.6 0.17 280)",
    },
    dark: {
      background: "oklch(0.17 0.02 300)",
      foreground: "oklch(0.94 0.01 300)",
      card: "oklch(0.21 0.02 300)",
      cardForeground: "oklch(0.94 0.01 300)",
      popover: "oklch(0.21 0.02 300)",
      popoverForeground: "oklch(0.94 0.01 300)",
      primary: "oklch(0.66 0.18 295)",
      primaryForeground: "oklch(0.16 0.02 300)",
      secondary: "oklch(0.28 0.03 300)",
      secondaryForeground: "oklch(0.92 0.01 300)",
      muted: "oklch(0.27 0.02 300)",
      mutedForeground: "oklch(0.7 0.02 300)",
      accent: "oklch(0.28 0.03 300)",
      accentForeground: "oklch(0.92 0.01 300)",
      destructive: "oklch(0.52 0.2 27.3)",
      destructiveForeground: "oklch(0.94 0.01 300)",
      border: "oklch(0.3 0.02 300)",
      input: "oklch(0.3 0.02 300)",
      ring: "oklch(0.66 0.18 295)",
      sidebar: "oklch(0.21 0.02 300)",
      sidebarForeground: "oklch(0.94 0.01 300)",
      sidebarPrimary: "oklch(0.66 0.18 295)",
      sidebarPrimaryForeground: "oklch(0.16 0.02 300)",
      sidebarAccent: "oklch(0.28 0.03 300)",
      sidebarAccentForeground: "oklch(0.92 0.01 300)",
      sidebarBorder: "oklch(0.3 0.02 300)",
      sidebarRing: "oklch(0.66 0.18 295)",
      chart1: "oklch(0.66 0.18 295)",
      chart2: "oklch(0.72 0.15 320)",
      chart3: "oklch(0.64 0.14 270)",
      chart4: "oklch(0.74 0.16 310)",
      chart5: "oklch(0.68 0.17 280)",
    },
  },
];

export function getColorScheme(id: string): ColorScheme | undefined {
  return colorSchemes.find((scheme) => scheme.id === id);
}

export const DEFAULT_COLOR_SCHEME_ID = "trust";
export const STORAGE_KEY_MODE = "webtemplate-theme-preference";
export const STORAGE_KEY_SCHEME = "webtemplate-color-scheme";

/**
 * Server-readable theme cookie shared with the no-FOUC pre-paint script
 * (`no-fouc-script.ts`). That script reads `document.cookie`'s `theme` value
 * with the enum `light` | `dark` | `system` BEFORE first paint, so this cookie
 * is the authoritative persistence path for an explicit theme choice (GAP-01).
 *
 * The provider's internal `ThemeMode` uses `auto` where the cookie uses
 * `system`; the helpers below bridge the two enums. Do NOT change the cookie
 * name or enum here — the no-FOUC reader's contract is fixed.
 */
export const THEME_COOKIE_NAME = "theme";

type ThemeCookieValue = "light" | "dark" | "system";

/** Durable persistence: ~1 year, path=/, SameSite=Lax so the server can read it. */
const THEME_COOKIE_MAX_AGE_SECONDS = 60 * 60 * 24 * 365;

/** Map the provider's internal mode (`auto`) to the cookie enum (`system`). */
export function modeToCookieValue(mode: ThemeMode): ThemeCookieValue {
  return mode === "auto" ? "system" : mode;
}

/** Map the cookie enum (`system`) back to the provider's internal mode (`auto`). */
export function cookieValueToMode(value: string): ThemeMode | null {
  if (value === "light" || value === "dark") return value;
  if (value === "system") return "auto";
  return null;
}

/** Read the `theme` cookie and return the provider mode, or null if absent/invalid. */
export function readThemeCookieMode(): ThemeMode | null {
  if (typeof document === "undefined") return null;
  const match = document.cookie.match(/(?:^|; )theme=([^;]*)/);
  if (!match) return null;
  return cookieValueToMode(decodeURIComponent(match[1] ?? ""));
}

/** Persist the mode to the server-readable `theme` cookie the no-FOUC script reads. */
export function writeThemeCookie(mode: ThemeMode): void {
  if (typeof document === "undefined") return;
  const value = modeToCookieValue(mode);
  document.cookie = `${THEME_COOKIE_NAME}=${value}; path=/; max-age=${THEME_COOKIE_MAX_AGE_SECONDS}; SameSite=Lax`;
}
